import axios, {AxiosError} from 'axios'
import {FC, useEffect, useState} from 'react'
import Select, {FormatOptionLabelMeta, SingleValue} from 'react-select'
import {useIsOnPremises} from '../../../../../auth/core/useOnPremises'
import {
  getTaxWorkspaceInvoiceContractChoices,
  readWorkspaceError,
  WorkspaceRequestError,
} from '../../../../tax-workspace/core/api'
import {CONTRACT_TYPE_LABELS} from '../../../../tax-workspace/core/enums'
import {
  TaxWorkspaceInvoiceContractChoice,
  TaxWorkspaceInvoiceContractChoices,
} from '../../../../tax-workspace/core/models'
import {formatPersianDateTime} from '../../../../tax-workspace/core/tehranDate'
import {THIRD_PARTY_CONTRACT_NUMBER_LENGTH} from '../../core/invoicePatternValidation'

type Props = {
  invoiceTemplate: number
  /** The environment the invoice goes to; undefined lets the server use the business default. */
  productionInvoice?: boolean
  value?: string
  onPick: (contractNumber: string) => void
}

type LoadState =
  | {status: 'loading'}
  | {status: 'ready'; data: TaxWorkspaceInvoiceContractChoices}
  | {status: 'error'; error: WorkspaceRequestError}
  | {status: 'absent'}

const INPUT_ID = 'third-party-contract-picker'

const typeLabel = (type?: string | null) => (type ? CONTRACT_TYPE_LABELS[type] || type : '')

const formatOptionLabel = (
  contract: TaxWorkspaceInvoiceContractChoice,
  {context}: FormatOptionLabelMeta<TaxWorkspaceInvoiceContractChoice>
) =>
  context === 'value' ? (
    <span>
      <span dir='ltr'>{contract.contractBrokerageNumber}</span>
      {contract.title ? ` — ${contract.title}` : ''}
    </span>
  ) : (
    <span className='d-block'>
      <span className='d-flex justify-content-between gap-3'>
        <span className='fw-semibold text-truncate'>
          {contract.title || typeLabel(contract.type)}
        </span>
        <span className='fw-bold text-nowrap' dir='ltr'>
          {contract.contractBrokerageNumber}
        </span>
      </span>
      <span className='d-block text-gray-600 fs-7'>
        {contract.brokerClientName ? `آمر: ${contract.brokerClientName}` : 'آمر: —'}
        {contract.issueDate
          ? `، تاریخ عقد ${formatPersianDateTime(contract.issueDate, false)}`
          : ''}
        {contract.type ? `، ${typeLabel(contract.type)}` : ''}
      </span>
      {!contract.selectable && contract.reason && (
        <span className='d-block text-warning fs-7'>{contract.reason}</span>
      )}
    </span>
  )

/**
 * Offers the approved third-party contracts of the business's tax workspace for bsrn. The
 * number field above it stays the source of truth: choosing a contract only fills it, and any
 * number can still be typed by hand.
 */
const ThirdPartyContractPicker: FC<Props> = ({
  invoiceTemplate,
  productionInvoice,
  value,
  onPick,
}) => {
  // An on-premises install has no کارپوشه: no contracts to offer, and no request for them.
  const onPremises = useIsOnPremises()
  const [state, setState] = useState<LoadState>(
    onPremises ? {status: 'absent'} : {status: 'loading'}
  )
  const [reload, setReload] = useState(0)

  useEffect(() => {
    if (onPremises) return
    const controller = new AbortController()
    setState({status: 'loading'})
    getTaxWorkspaceInvoiceContractChoices(
      {pattern: invoiceTemplate, production: productionInvoice, refresh: reload > 0},
      controller.signal
    )
      .then((data) => setState({status: 'ready', data}))
      .catch((error) => {
        if (axios.isCancel(error)) return
        // A server older than the picker (backend 1.0.0.589) has no such route: show nothing.
        if ((error as AxiosError)?.response?.status === 404) {
          setState({status: 'absent'})
          return
        }
        setState({
          status: 'error',
          error: readWorkspaceError(error, 'فهرست قراردادهای کارپوشه خوانده نشد.'),
        })
      })
    return () => controller.abort()
  }, [invoiceTemplate, productionInvoice, reload, onPremises])

  if (state.status === 'loading') {
    return (
      <div className='text-gray-600 fs-7 mt-3' role='status'>
        <span className='spinner-border spinner-border-sm align-middle me-2' aria-hidden='true' />
        در حال خواندن قراردادهای ثبت‌شده در کارپوشه…
      </div>
    )
  }

  if (state.status === 'absent') return null

  if (state.status === 'error') {
    const {error} = state
    return (
      <div className='text-warning fs-7 mt-3' role='alert'>
        فهرست قراردادهای کارپوشه خوانده نشد: {error.message} شماره را می‌توانید دستی وارد کنید.{' '}
        <button
          type='button'
          className='btn btn-link btn-sm p-0 fs-7 align-baseline'
          onClick={() => setReload((count) => count + 1)}
        >
          تلاش مجدد
        </button>
        {error.correlationId && (
          <>
            {'، '}
            <a
              href={`/tax-workspace/logs?cid=${encodeURIComponent(error.correlationId)}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              جزئیات در گزارش کارپوشه
            </a>
          </>
        )}
      </div>
    )
  }

  const {data} = state
  if (!data.available) {
    if (data.unavailableCode === 'tax_workspace_disabled') return null
    return (
      <div className='text-gray-600 fs-7 mt-3'>
        <i className='las la-info-circle fs-6 align-middle me-1' aria-hidden='true' />
        فهرست قراردادهای کارپوشه در دسترس نیست: {data.unavailableMessage}
      </div>
    )
  }

  const contracts = data.contracts || []
  const typed = (value || '').trim()
  const match = contracts.find((contract) => contract.contractBrokerageNumber === typed)
  const selectableCount = contracts.filter((contract) => contract.selectable).length
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

  let feedback: {className: string; text: string} | null = null
  if (typed.length === THIRD_PARTY_CONTRACT_NUMBER_LENGTH) {
    if (match?.selectable) {
      feedback = {
        className: 'text-success',
        text: `این شماره متعلق به قرارداد ${match.title ? `«${match.title}» ` : ''}${
          match.brokerClientName ? `با آمر ${match.brokerClientName} ` : ''
        }در کارپوشه است.`,
      }
    } else if (match) {
      feedback = {className: 'text-warning', text: match.reason || ''}
    } else if (!data.truncated) {
      feedback = {
        className: 'text-gray-600',
        text: 'این شماره در قراردادهای ثبت نهایی کارپوشه پیدا نشد؛ اگر از درستی آن مطمئن هستید، می‌توانید ادامه دهید.',
      }
    }
  }

  return (
    <div className='mt-3'>
      <label className='fs-7 fw-semibold text-gray-700 mb-1' htmlFor={INPUT_ID}>
        انتخاب از قراردادهای ثبت نهایی در کارپوشه
      </label>
      <Select<TaxWorkspaceInvoiceContractChoice, false>
        inputId={INPUT_ID}
        classNamePrefix='select'
        isRtl
        isClearable
        isDisabled={contracts.length === 0}
        placeholder={
          contracts.length === 0
            ? 'قرارداد ثبت نهایی‌شده‌ای در کارپوشه نیست'
            : 'قرارداد را جستجو و انتخاب کنید'
        }
        noOptionsMessage={() => 'قراردادی با این مشخصات پیدا نشد.'}
        options={contracts}
        isOptionDisabled={(contract) => !contract.selectable}
        value={match?.selectable ? match : null}
        onChange={(contract: SingleValue<TaxWorkspaceInvoiceContractChoice>) =>
          onPick(contract ? contract.contractBrokerageNumber : '')
        }
        getOptionValue={(contract) => contract.contractBrokerageNumber}
        getOptionLabel={(contract) =>
          [contract.contractBrokerageNumber, contract.title, contract.brokerClientName]
            .filter(Boolean)
            .join(' ')
        }
        formatOptionLabel={formatOptionLabel}
        menuPosition='fixed'
        menuPortalTarget={menuPortalTarget}
        styles={{
          menuPortal: (base) => ({...base, zIndex: 10000}),
          menu: (base) => ({...base, zIndex: 10000}),
        }}
      />
      {feedback && (
        <div className={`${feedback.className} fs-7 mt-1`} aria-live='polite'>
          {feedback.text}
        </div>
      )}
      <div className='d-flex flex-wrap align-items-center gap-2 text-gray-600 fs-7 mt-1'>
        <span>
          {contracts.length === 0
            ? 'شماره را می‌توانید دستی وارد کنید.'
            : `${selectableCount.toLocaleString('fa-IR')} قرارداد قابل انتخاب${
                data.truncated
                  ? '؛ فهرست کامل نیست و اگر قرارداد شما در آن نیست، شماره را دستی وارد کنید'
                  : ''
              }.`}
        </span>
        <button
          type='button'
          className='btn btn-link btn-sm p-0 fs-7'
          onClick={() => setReload((count) => count + 1)}
        >
          به‌روزرسانی فهرست
        </button>
      </div>
    </div>
  )
}

export {ThirdPartyContractPicker}
